import { NavLink } from 'react-router';
import { LayoutDashboard, LogOut, PanelLeftClose, PanelLeftOpen, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCurrentUser } from '@/features/auth/useCurrentUser';
import { signOut } from '@/lib/supabase/auth';
import { ThemeSwitch } from '@/components/ThemeSwitch';
import { NotificationBell } from '@/features/notifications/NotificationBell';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

/**
 * Grouped rather than a flat list, because the groups are the point: this is where new
 * sections land, and an unlabelled list of six links has nowhere obvious to put a
 * seventh.
 */
const SECTIONS: ReadonlyArray<{ heading: string; items: readonly NavItem[] }> = [
  {
    heading: 'Work',
    items: [{ to: '/board', label: 'Board', icon: LayoutDashboard }],
  },
  {
    heading: 'Manage',
    items: [{ to: '/admin', label: 'People', icon: Users, adminOnly: true }],
  },
];

export function Sidebar({
  collapsed = false,
  onToggleCollapse,
  onNavigate,
}: {
  collapsed?: boolean;
  onToggleCollapse?: (() => void) | undefined;
  onNavigate?: (() => void) | undefined;
}) {
  const { data: me } = useCurrentUser();

  const sections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.adminOnly || me?.isAdmin),
  })).filter((section) => section.items.length > 0);

  return (
    <div
      className="flex h-full flex-col border-r transition-[width] duration-200"
      style={{
        width: collapsed ? '4rem' : '16rem',
        borderColor: 'var(--border)',
        background: 'var(--sidebar)',
      }}
    >
      <div className={`flex items-center gap-2.5 py-4 ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
        <Logo size={30} />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight">
              Tap Academy
            </span>
            {onToggleCollapse && (
              <CollapseButton collapsed={collapsed} onClick={onToggleCollapse} />
            )}
          </>
        )}
      </div>

      {collapsed && onToggleCollapse && (
        <div className="flex justify-center pb-1">
          <CollapseButton collapsed={collapsed} onClick={onToggleCollapse} />
        </div>
      )}

      <nav className="min-h-0 flex-1 overflow-y-auto no-scrollbar px-2 pb-2">
        {sections.map((section) => (
          <div key={section.heading} className="mb-4">
            {collapsed ? (
              // A heading has nowhere to go at 4rem, so the grouping survives as a rule.
              <div className="mx-2 mb-2 border-t" style={{ borderColor: 'var(--border)' }} />
            ) : (
              <p
                className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                style={{ color: 'var(--text-faint)' }}
              >
                {section.heading}
              </p>
            )}

            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                // Collapsed, the label is the only thing naming the icon — so it moves
                // to a tooltip rather than disappearing.
                title={collapsed ? item.label : undefined}
                className={`mb-0.5 flex items-center gap-2.5 rounded-lg py-2 text-sm transition-colors ${
                  collapsed ? 'justify-center px-0' : 'px-2'
                }`}
                style={({ isActive }) => ({
                  background: isActive ? 'var(--accent-soft)' : 'transparent',
                  color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                  fontWeight: isActive ? 600 : 400,
                })}
              >
                <item.icon size={17} className="shrink-0" />
                {!collapsed && item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t p-2" style={{ borderColor: 'var(--border)' }}>
        {/* Desktop only. On a phone this same component renders in the app header,
            and two bells with two badges is one bell too many. */}
        <div className={`hidden lg:block ${collapsed ? 'flex justify-center' : ''}`}>
          <NotificationBell collapsed={collapsed} />
        </div>

        <div className={collapsed ? 'flex justify-center' : ''}>
          <ThemeSwitch compact={collapsed} />
        </div>

        <div className={`flex items-center gap-2.5 rounded-lg py-2 ${collapsed ? 'justify-center px-0' : 'px-2'}`}>
          <Avatar name={me?.email ?? ''} url={null} size={28} />

          {!collapsed && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium">{me?.email}</span>
              {me?.isAdmin && (
                <span className="block text-[10px]" style={{ color: 'var(--text-faint)' }}>
                  Admin
                </span>
              )}
            </span>
          )}

          {!collapsed && (
            <button
              type="button"
              onClick={() => void signOut()}
              title="Sign out"
              aria-label="Sign out"
              className="flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:opacity-70"
              style={{ color: 'var(--text-muted)' }}
            >
              <LogOut size={16} />
            </button>
          )}
        </div>

        {collapsed && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => void signOut()}
              title="Sign out"
              aria-label="Sign out"
              className="flex size-10 items-center justify-center rounded-lg transition-colors hover:opacity-70"
              style={{ color: 'var(--text-muted)' }}
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The mark is a black disc with a white wordmark, so on a near-black sidebar it would
 * be a black hole. Inverted in dark mode it becomes a white disc with a black
 * wordmark — the standard treatment for a monochrome logo, and it stays the same
 * shape rather than being recoloured into something that is not the brand.
 */
function Logo({ size }: { size: number }) {
  return (
    <img
      src="/logo.svg"
      alt="Tap Academy"
      width={size}
      height={size}
      className="shrink-0 dark-invert"
      style={{ width: size, height: size }}
    />
  );
}

function CollapseButton({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      className="flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:opacity-70"
      style={{ color: 'var(--text-muted)' }}
    >
      {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
    </button>
  );
}

/** Falls back to an initial, because a Google photo URL can 404 after an account change. */
export function Avatar({
  name,
  url,
  size = 24,
}: {
  name: string;
  url: string | null;
  size?: number;
}) {
  const initial = (name.trim()[0] ?? '?').toUpperCase();

  return url ? (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      className="shrink-0 rounded-full object-cover"
      style={{ width: size, height: size, background: 'var(--surface-sunken)' }}
    />
  ) : (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full font-medium"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        background: 'var(--surface-sunken)',
        color: 'var(--text-muted)',
      }}
    >
      {initial}
    </span>
  );
}
